import React, { useMemo } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft } from "lucide-react";
import api from "@/lib/api";
import { Skeleton } from "@/components/ui/skeleton";
import { badgeStyle } from "@/lib/badgeColor";
import TonalBadge from "@/components/TonalBadge";
import { useMasterList } from "@/hooks/useMasterData";

// Canonical Lead Status column order for the board.
const LEAD_STATUS_ORDER = [
  "New",
  "Contacted",
  "Qualified",
  "Active (Matching in Progress)",
  "On Hold",
  "Closed Won (Converted)",
  "Closed Lost",
  "Repeat Client",
];

function fmtBudget(min, max, unit) {
  const hasMin = min != null && min !== "" && Number(min) > 0;
  const hasMax = max != null && max !== "" && Number(max) > 0;
  if (!hasMin && !hasMax) return null;
  const u = unit ? ` ${unit}` : "";
  const n = (v) => Number(v).toLocaleString("en-IN");
  if (hasMin && hasMax) return `₹${n(min)}${u} – ₹${n(max)}${u}`;
  return `₹${n(hasMin ? min : max)}${u}`;
}

function LeadCard({ l, unitLabel, onClick }) {
  const budget = fmtBudget(l.budget_min, l.budget_max, unitLabel[l.budget_unit_id]);
  return (
    <button
      type="button"
      onClick={() => onClick(l.id)}
      className="text-left w-full bg-white border border-slate-200 rounded-md p-3 shadow-sm hover:shadow-md transition-shadow"
      data-testid={`group-kanban-card-${l.id}`}
    >
      <div className="font-mono-num text-[10px] text-slate-500">{l.lead_code}</div>
      <div className="text-sm font-semibold text-slate-900 truncate">{l.lead_name}</div>
      {l.company_name && <div className="text-xs text-slate-500 truncate">{l.company_name}</div>}
      <div className="mt-1.5 font-mono-num text-xs text-slate-700">
        {budget || <span className="text-slate-300">—</span>}
      </div>
      {l.lead_warmth_id && (
        <div className="mt-2">
          <TonalBadge listType="lead_warmth" id={l.lead_warmth_id} />
        </div>
      )}
    </button>
  );
}

export default function LeadGroupKanban({ scope = "individual" }) {
  const isClient = scope === "institutional";
  const groupsBase = isClient ? "/client-groups" : "/lead-groups";
  const detailBase = isClient ? "/clients" : "/leads";
  const { groupId } = useParams();
  const navigate = useNavigate();

  const { data: groups = [] } = useQuery({
    queryKey: ["lead-groups"],
    queryFn: async () => (await api.get("/lead-groups")).data,
  });
  const group = useMemo(() => groups.find((g) => g.id === groupId), [groups, groupId]);

  const { data: leads = [], isLoading } = useQuery({
    queryKey: ["group-leads", groupId],
    queryFn: async () => (await api.get("/leads", { params: { group_id: groupId } })).data,
  });

  const { data: statuses = [] } = useMasterList("lead_status");
  const { data: priceUnits = [] } = useMasterList("price_unit");
  const unitLabel = useMemo(() => Object.fromEntries(priceUnits.map((d) => [d.id, d.label])), [priceUnits]);

  // Build the ordered columns from the canonical status order, resolving each
  // label to its taxonomy value (id + Badge Color).
  const columns = useMemo(() => {
    const byLabel = Object.fromEntries(statuses.map((s) => [s.label, s]));
    return LEAD_STATUS_ORDER.map((label) => {
      const s = byLabel[label];
      return { label, id: s?.id, color: s?.badge_color };
    });
  }, [statuses]);

  const byStatus = useMemo(() => {
    const map = {};
    for (const l of leads) {
      (map[l.lead_status_id] = map[l.lead_status_id] || []).push(l);
    }
    return map;
  }, [leads]);

  return (
    <div className="p-8 pb-12" data-testid="lead-group-kanban">
      <Link to={groupsBase} className="text-xs text-slate-500 hover:text-slate-900 inline-flex items-center gap-1" data-testid="back-to-groups">
        <ArrowLeft className="h-3.5 w-3.5" /> Back to Lead Groups
      </Link>

      <div className="mt-2">
        <div className="label-eyebrow">Lead Group · Pipeline</div>
        <h1 className="font-display text-3xl font-bold text-slate-900 mt-1" data-testid="group-kanban-title">
          {group ? group.name : "Lead Group"}
        </h1>
        <p className="text-sm text-slate-600 mt-1.5">
          Read-only view of this group's leads by Lead Status.
        </p>
      </div>

      {isLoading ? (
        <div className="mt-6 flex gap-3">
          {LEAD_STATUS_ORDER.map((s) => (
            <div key={s} className="w-72 shrink-0">
              <div className="label-eyebrow mb-2">{s}</div>
              <Skeleton className="h-32 w-full" />
            </div>
          ))}
        </div>
      ) : leads.length === 0 ? (
        <div className="mt-8 bg-white border border-slate-200 rounded-md py-16 text-center" data-testid="group-kanban-empty">
          <div className="text-sm font-medium text-slate-700">No leads in this group yet</div>
          <div className="text-xs text-slate-500 mt-1">Add leads to this group to see them on the board.</div>
        </div>
      ) : (
        <div className="mt-6 overflow-x-auto scrollbar-thin">
          <div className="flex gap-3 min-w-max pb-4" data-testid="group-kanban-board">
            {columns.map((col) => {
              const cards = (col.id && byStatus[col.id]) || [];
              return (
                <div key={col.label} className="w-72 shrink-0 bg-slate-100 rounded-md p-2" data-testid={`group-kanban-column-${col.label}`}>
                  <div className="flex items-center justify-between px-1 py-1.5">
                    <span
                      style={badgeStyle(col.color)}
                      className="inline-flex items-center px-2.5 py-0.5 rounded-full text-[11px] font-semibold border"
                      data-testid={`group-kanban-col-header-${col.label}`}
                    >
                      {col.label}
                    </span>
                    <span className="font-mono-num text-[10px] font-semibold text-slate-500 bg-white px-1.5 py-0.5 rounded">
                      {cards.length}
                    </span>
                  </div>
                  <div className="space-y-2">
                    {cards.length === 0 ? (
                      <div className="text-[11px] text-slate-400 text-center py-3">—</div>
                    ) : cards.map((l) => (
                      <LeadCard key={l.id} l={l} unitLabel={unitLabel} onClick={(id) => navigate(`${detailBase}/${id}`)} />
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
